import {
  photoSubmissionCrudConfig,
  type PhotoSubmission,
} from '@/config/objects/photo-submissions';
import { CrudListView, type CrudConfig } from '@startupquickstart/frontend';
import { type ReactNode, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';

interface PhotoSubmissionsListProps {
  /**
   * Optional custom config (with modified endpoints, actions, etc.)
   */
  config?: CrudConfig<PhotoSubmission>;
  /**
   * Optional default where clause for filtering
   */
  defaultWhere?: Record<string, unknown>;
  /**
   * Optional custom onItemClick handler
   */
  onItemClick?: (photoSubmission: PhotoSubmission) => void;
  /**
   * Custom class name for the container
   */
  className?: string;
  /**
   * Base URL for navigation, defaults to /photo-submissions
   */
  baseUrl?: string;
  /**
   * Breadcrumbs for navigation
   */
  breadcrumbs?: Array<{ label: string; href?: string; current?: boolean }>;
  /**
   * Content rendered in the list header's right-hand area — used here for the
   * status segmented control so it sits inline with the header.
   */
  headerRight?: ReactNode;
}

/**
 * Reusable photo submissions list component
 * Pass a custom config with modified actions to customize behavior
 */
export function PhotoSubmissionsList({
  config,
  baseUrl = '/photo-submissions',
  defaultWhere,
  onItemClick,
  className,
  breadcrumbs,
  headerRight,
}: PhotoSubmissionsListProps) {
  const navigate = useNavigate();

  const _onItemClick = useCallback(
    (photoSubmission: PhotoSubmission) => {
      if (onItemClick) {
        onItemClick(photoSubmission);
      } else {
        navigate(`/photo-submissions/${photoSubmission.id}`);
      }
    },
    [navigate, onItemClick],
  );

  return (
    <div className={className}>
      <CrudListView
        config={config || photoSubmissionCrudConfig}
        baseUrl={baseUrl}
        onItemClick={_onItemClick}
        defaultWhere={defaultWhere}
        breadcrumbs={breadcrumbs}
        headerRight={headerRight}
        getValueFromItem={(photoSubmission: PhotoSubmission) => photoSubmission.id}
        getLabelFromItem={(photoSubmission: PhotoSubmission) =>
          `${photoSubmission.entityType} - ${photoSubmission.entityName || photoSubmission.entityId}`
        }
      />
    </div>
  );
}
